import { useState, useRef } from 'react';
import { parseFile } from '../utils/fileParser';
import { recognizeImage } from '../utils/ocr';

export default function UploadPage({ library, onLoad, onOpen, onDelete, onRename }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  function startEdit(b) {
    setEditingId(b.id);
    setEditingTitle(b.title);
  }

  function commitEdit() {
    if (editingId && editingTitle.trim()) onRename(editingId, editingTitle.trim());
    setEditingId(null);
  }

  function handleEditKey(e) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditingId(null);
  }
  const inputRef = useRef();
  const cameraRef = useRef();

  const handlePhoto = async (file) => {
    if (!file) return;
    setLoading(true);
    setOcrProgress({ phase: 'download', pct: 0 });
    setError('');
    try {
      const text = await recognizeImage(file, setOcrProgress);
      if (!text) throw new Error('No text found in image');
      const title = 'Photo – ' + new Date().toLocaleDateString('ja-JP');
      onLoad({ text, title });
    } catch (err) {
      setError(err.message || 'Failed to read image');
      setLoading(false);
    }
    setOcrProgress(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const text = await parseFile(file);
      onLoad({ text, title: file.name.replace(/\.[^.]+$/, '') });
    } catch (err) {
      setError(err.message || 'Failed to read file');
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <h1 style={{ fontFamily: 'Georgia, "Hiragino Mincho ProN", serif', fontSize: 42, fontWeight: 700, letterSpacing: 6, marginBottom: 6 }}>
        YOMOU
      </h1>
      <p style={{ color: '#999', fontSize: 14, marginBottom: 48 }}>読もう · Japanese reading tool</p>

      {library.length > 0 && (
        <div style={{ width: '100%', maxWidth: 400, marginBottom: 32 }}>
          <p style={{ color: '#bbb', fontSize: 11, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 2 }}>Your books</p>
          {library.map(b => (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center',
              background: 'white', border: '1px solid #e8e6e1', borderRadius: 10,
              padding: '11px 14px', marginBottom: 8, gap: 10,
            }}>
              {editingId === b.id ? (
                <input
                  autoFocus
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={handleEditKey}
                  style={{ flex: 1, fontSize: 14, color: '#333', border: 'none', outline: 'none', background: 'transparent', minWidth: 0 }}
                />
              ) : (
                <span style={{ flex: 1, fontSize: 14, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.title}
                </span>
              )}
              <button
                onClick={() => startEdit(b)}
                style={{ background: 'none', border: 'none', color: '#ccc', fontSize: 14, cursor: 'pointer', padding: '10px 8px', flexShrink: 0, margin: '-10px 0' }}
                title="Rename"
              >✎</button>
              <button
                onClick={() => onOpen(b.id)}
                style={{ background: '#333', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}
              >
                Open
              </button>
              <button
                onClick={() => onDelete(b.id)}
                style={{ background: 'none', border: 'none', color: '#ccc', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '10px 8px', flexShrink: 0, margin: '-10px 0' }}
                title="Remove from library"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        onClick={() => !loading && inputRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        style={{
          border: `2px dashed ${dragging ? '#555' : '#d0cec9'}`,
          borderRadius: 16,
          padding: '48px 80px',
          cursor: loading ? 'wait' : 'pointer',
          background: dragging ? '#f0ede8' : 'white',
          textAlign: 'center',
          transition: 'all 0.15s',
          maxWidth: 400,
          width: '100%',
        }}
      >
        {loading ? (
          <>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{ocrProgress ? '📷' : '⏳'}</div>
            {ocrProgress ? (
              <>
                <p style={{ color: '#888', margin: 0, fontSize: 15 }}>
                  {ocrProgress.phase === 'download' ? 'Downloading Japanese model…' : 'Reading text…'}
                </p>
                <div style={{ width: 160, height: 4, background: '#f0ede8', borderRadius: 2, margin: '12px auto 0' }}>
                  <div style={{ height: '100%', background: '#4a90d9', borderRadius: 2, width: `${ocrProgress.pct}%`, transition: 'width 0.2s' }} />
                </div>
                <p style={{ color: '#bbb', fontSize: 12, margin: '8px 0 0' }}>{ocrProgress.pct}%</p>
              </>
            ) : (
              <p style={{ color: '#888', margin: 0, fontSize: 15 }}>Parsing file…</p>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📖</div>
            <p style={{ fontWeight: 600, color: '#333', fontSize: 16, margin: 0 }}>Open a file</p>
            <p style={{ color: '#aaa', fontSize: 13, marginTop: 8, margin: '8px 0 0' }}>tap, click, or drop here</p>
            <p style={{ color: '#bbb', fontSize: 12, marginTop: 12 }}>TXT · PDF · EPUB</p>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".txt,.pdf,.epub"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])}
      />

      <button
        onClick={() => !loading && cameraRef.current.click()}
        disabled={loading}
        style={{
          marginTop: 16,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: '1px solid #e8e6e1', borderRadius: 12,
          padding: '12px 24px', cursor: loading ? 'wait' : 'pointer',
          color: '#555', fontSize: 14, fontWeight: 500,
        }}
      >
        <span style={{ fontSize: 20 }}>📷</span> Scan photo
      </button>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={e => handlePhoto(e.target.files[0])}
      />

      {error && (
        <p style={{ color: '#c0392b', marginTop: 20, fontSize: 14, maxWidth: 400, textAlign: 'center' }}>{error}</p>
      )}
    </div>
  );
}
